import { useCallback, useState } from "react"

import {
  CRITERION_VALUE,
  type CriterionValue,
} from "@/constants/criterion-values"

interface UseCriterionRowInteractionsProps {
  /** Invoked when a vote keyboard shortcut (M / N / X) is pressed on a row. */
  onCriterionChange: (index: number, value: CriterionValue) => void
}

interface UseCriterionRowInteractionsReturn {
  /** Indices of criteria whose clamped text is currently expanded. */
  expandedCriteria: Set<number>
  /** Toggle the expanded state of the criterion at `index`. */
  toggleExpanded: (index: number) => void
  /** Keyboard handler for a criterion sub-row (votes, expand toggle, navigation). */
  handleSubRowKeyDown: (
    event: React.KeyboardEvent<HTMLTableRowElement>,
    index: number,
  ) => void
}

/**
 * Expand/collapse and keyboard-interaction state for composite-evaluation
 * criterion sub-rows.
 *
 * Tracks which criterion rows have their clamped text expanded and maps
 * keyboard shortcuts to votes (M/N/X), text expansion (R, only when clamped),
 * and arrow-key row navigation.
 */
export const useCriterionRowInteractions = ({
  onCriterionChange,
}: UseCriterionRowInteractionsProps): UseCriterionRowInteractionsReturn => {
  const [expandedCriteria, setExpandedCriteria] = useState<Set<number>>(
    new Set(),
  )

  const toggleExpanded = useCallback((index: number) => {
    setExpandedCriteria((prev) => {
      const next = new Set(prev)
      if (next.has(index)) {
        next.delete(index)
      } else {
        next.add(index)
      }
      return next
    })
  }, [])

  const handleSubRowKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTableRowElement>, index: number) => {
      if (e.key === "m" || e.key === "M") {
        e.preventDefault()
        e.stopPropagation()
        onCriterionChange(index, CRITERION_VALUE.MET)
        return
      }

      if (e.key === "n" || e.key === "N") {
        e.preventDefault()
        e.stopPropagation()
        onCriterionChange(index, CRITERION_VALUE.NOT_MET)
        return
      }

      if (e.key === "x" || e.key === "X") {
        e.preventDefault()
        e.stopPropagation()
        onCriterionChange(index, CRITERION_VALUE.UNKNOWN)
        return
      }

      // R → toggle expand criterion text (only if text is actually clamped)
      if (e.key === "r" || e.key === "R") {
        e.preventDefault()
        e.stopPropagation()
        const textEl =
          e.currentTarget.querySelector<HTMLElement>(".criterion-text")
        if (textEl) {
          const isClamped = textEl.scrollHeight > textEl.clientHeight
          const isCurrentlyExpanded = expandedCriteria.has(index)
          if (isClamped || isCurrentlyExpanded) {
            toggleExpanded(index)
          }
        }
        return
      }

      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault()
        const currentRow = e.currentTarget
        const allRows = Array.from(
          currentRow.parentElement?.querySelectorAll<HTMLElement>(
            "tr.metric-row",
          ) || [],
        )
        const currentIndex = allRows.indexOf(currentRow)

        if (e.key === "ArrowDown") {
          const nextRow = allRows[currentIndex + 1]
          if (nextRow) {
            nextRow.focus()
          }
        } else {
          const prevRow = allRows[currentIndex - 1]
          if (prevRow) {
            prevRow.focus()
          }
        }
      }
    },
    [onCriterionChange, toggleExpanded, expandedCriteria],
  )

  return { expandedCriteria, toggleExpanded, handleSubRowKeyDown }
}
